import yaml from 'js-yaml';

// Import the YAML file as a string (webpack will handle this)
// Note: You'll need to add a custom webpack loader or use a different approach
// For now, we'll define the types and create a function to load the config

export interface Skill {
  name: string;
  icon?: string; // Simple-icons icon name
  rating: number;
  tags: string[];
  categoryTags: string[];
}

export interface Experience {
  title: string;
  organization: string;
  location: string;
  period: string;
  description: string;
  achievements: string[];
  type?: 'work' | 'education'; // Optional type field for timeline display
}

export interface JobRole {
  title: string;
  company: string;
  compatibility: number;
  description: string;
  skills: string[];
  benefits: string[];
  icon?: React.ReactNode; // Optional icon for frontend use
}

export interface Contact {
  email: string;
  linkedin: string;
  github: string;
  location: string;
}

export interface ProfileConfig {
  name: string;
  title: string;
  tagline: string;
  skills: Skill[];
  experience: Experience[];
  skillTags: string[];
  contact: Contact;
  jobRoles: JobRole[];
}

// For development, we'll load the YAML file dynamically
// In production, you might want to bundle this differently
export const loadProfileConfig = async (): Promise<ProfileConfig> => {
  try {
    // In a real setup, you'd fetch this from a public URL or bundle it
    // For now, we'll return the data directly
    const response = await fetch('/config/profile.yaml');
    const yamlText = await response.text();
    return yaml.load(yamlText) as ProfileConfig;
  } catch (error) {
    console.warn('Failed to load YAML config, using fallback data:', error);
    // Fallback to hardcoded data if YAML loading fails
    return getFallbackConfig();
  }
};

// Fallback configuration (same as YAML but in TypeScript)
const getFallbackConfig = (): ProfileConfig => ({
  name: "Anshuman Rohella",
  title: "I Build, I Break, I Build Again",
  tagline: "Any fool can write code that a computer can understand. Good programmers write code that humans can understand. - Martin Fowler",
  skills: [
    { name: 'Go', icon: 'go', rating: 4, tags: ['languages'], categoryTags: ['concurrency', 'microservices', 'networking'] },
    { name: 'AWS', icon: 'amazonaws', rating: 4, tags: ['Cloud Native'], categoryTags: ['S3', 'DynamoDB', 'AWS Lambdas', 'Container Registry'] },
    { name: 'Kubernetes', icon: 'kubernetes', rating: 3, tags: ['Cloud Native'], categoryTags: ['helm', 'orchestration', 'services'] },
    { name: 'GCP', icon: 'googlecloud', rating: 4, tags: ['Cloud Native'], categoryTags: ['k8s control plane', 'firebase', 'app distribution'] },
    { name: 'Microservice Architecture', icon: 'microservices', rating: 4, tags: ['Distributed Systems', 'Architecture'], categoryTags: ['API design', 'HA', 'scalability'] },
    { name: 'gRPC', icon: 'grpc', rating: 4, tags: ['Distributed Systems', 'Architecture'], categoryTags: ['event streaming', 'microservices'] },
    { name: 'Distributed Systems', icon: 'distributed', rating: 4, tags: ['Distributed Systems', 'Architecture'], categoryTags: ['CAP theorem', 'RAFT', 'leader election', 'distributed locks'] },
    { name: 'MongoDB', icon: 'mongodb', rating: 4, tags: ['Data Systems'], categoryTags: ['sharding', 'document storage', 'NoSQL'] },
    { name: 'Timeseries Databases', icon: 'prometheus', rating: 4, tags: ['Data Systems'], categoryTags: ['Prometheus', 'InfluxDB', 'monitoring'] },
    { name: 'Kafka', icon: 'apachekafka', rating: 4, tags: ['Distributed Systems', 'Architecture', 'Data System'], categoryTags: ['message queuing', 'event streaming', 'microservices'] },
    { name: 'Hashicorp Vault', icon: 'hashicorp', rating: 4, tags: ['Security'], categoryTags: ['Secret Management', 'Ephemeral Certificates'] },
    { name: 'AWS KMS', icon: 'amazonaws', rating: 4, tags: ['Security'], categoryTags: ['Secret Management', 'OSID', 'Oauth2'] },
    { name: 'Grafana', icon: 'grafana', rating: 4, tags: ['Devops'], categoryTags: ['LOKI', 'log aggregation', 'visualization', 'alerting'] },
    { name: 'Ansible', icon: 'ansible', rating: 4, tags: ['Devops'], categoryTags: ['configuration management', 'ansible vault', 'automation'] },
    { name: 'Terraform', icon: 'terraform', rating: 4, tags: ['Devops'], categoryTags: ['infrastructure', 'iac'] }
  ],
  experience: [
    {
      type: 'work',
      title: 'Software Engineer',
      organization: 'Apple Inc.',
      location: 'Tokyo, JP',
      period: '2023 - present',
      description: 'Developer Tools and OS Release Engineering',
      achievements: [
        'Served as a Release Engineer for operating system builds across multiple Apple platforms, including iOS, macOS, watchOS, and tvOS.',
        'Configured, maintained, and triaged build pipelines for OS images, SDK toolchains, and Xcode — ensuring stability and timely delivery of internal and public developer releases.',
        'Enhanced tooling and automation within the build system to streamline nightly builds, improve CI efficiency, and support large-scale release operations.',
        'Acted as a Directly Responsible Individual (DRI) for the WWDC 2024 public SDK and OS image release, coordinating across global teams to deliver high-impact developer-facing artifacts on schedule.',
        'Partnered with engineers and managers across different time zones to facilitate issue resolution, release validation, and multi-stage deployment workflows for millions of end users.'
      ]
    },
    {
      type: 'work',
      title: 'Platform Engineer',
      organization: 'Beacon Platform',
      location: 'Tokyo, JP',
      period: '2019 - 2023',
      description: 'Built resilient, cloud-agnostic infrastructure and automation tooling for hedge fund clients at Beacon Platform.',
      achievements: [
        'Engineered core platform features across Azure and AWS environments, enabling seamless multi-cloud deployment and operational consistency for global hedge fund clients.',
        'Designed and built integration tooling for cloud-native services (e.g., blob storage, managed databases, messaging queues), abstracting provider-specific APIs to deliver a cloud-agnostic platform experience for end users.',
        'Developed high-availability tooling for self-hosted Git server deployments, ensuring fault tolerance and disaster recovery across multiple availability zones and regions.',
        'Automated large-scale data migrations, including relational databases and network-attached file shares, through custom batch processing frameworks tailored for cross-region and cross-cloud workloads.'
      ]
    },
    {
      type: 'work',
      title: 'DevOps Engineer',
      organization: 'SBI Securities',
      location: 'Tokyo, JP',
      period: '2019-2021',
      description: 'Setup and maintained on various infrastructure components in a team of 3 for a B2B crypto wallet. Maintained the whole infrastructure and configuration as code.',
      achievements: [
        'Designed and setup stateful and stateless components for the whole infrastructure from early stages to 999 HA production stage.',
        'Wrote, optimized and automated legacy playbooks to make deployments, configuration and spinning up new environments faster, cutting the provisioning time from days to hours.',
        'Worked closely with developers/QA teams across the globe to provide functioning environments making sure smooth development and testing work flow.',
        'Worked closely with the business teams to cater to the infrastructure requirements for various business clients. Deployed and configured demo environments quickly and efficiently as per their needs.'
      ]
    }
  ],
  skillTags: ['all', 'Data Systems', 'Security', 'Cloud Native', 'Distributed Systems', 'Devops'],
  contact: {
    email: "anshuman.rohella@example.com",
    linkedin: "https://linkedin.com/in/anshumanrohella",
    github: "https://github.com/anshumanrohella",
    location: "Tokyo, Japan"
  },
  jobRoles: [
    {
      title: "Senior Full Stack Developer",
      company: "Tech Companies",
      compatibility: 95,
      description: "Perfect fit for companies looking for experienced developers who can handle both frontend and backend development.",
      skills: ["Node.js/Python", "Database Design", "API Development", "DevOps"],
      benefits: [
        "Can lead technical projects from start to finish",
        "Experience with modern development practices",
        "Strong problem-solving and debugging skills"
      ]
    },
    {
      title: "Software Architect",
      company: "Enterprise Companies",
      compatibility: 85,
      description: "Great fit for companies needing someone to design scalable software solutions.",
      skills: ["System Design", "Microservices", "Cloud Architecture", "Security", "Performance"],
      benefits: [
        "Experience with large-scale applications",
        "Understanding of enterprise patterns and practices",
        "Can make technical decisions that scale"
      ]
    },
    {
      title: "DevOps Engineer",
      company: "Startups & Scale-ups",
      compatibility: 80,
      description: "Perfect for companies looking to improve their deployment and infrastructure processes.",
      skills: ["CI/CD", "Docker/Kubernetes", "AWS/Azure", "Monitoring", "Automation"],
      benefits: [
        "Can set up and maintain robust deployment pipelines",
        "Experience with cloud infrastructure and scaling",
        "Focus on automation and efficiency"
      ]
    },
    {
      title: "Technical Lead",
      company: "Growing Companies",
      compatibility: 88,
      description: "Excellent for companies needing someone to lead technical teams and projects.",
      skills: ["Team Leadership", "Project Management", "Code Review", "Mentoring", "Architecture"],
      benefits: [
        "Proven leadership experience in previous roles",
        "Strong communication and collaboration skills",
        "Can balance technical excellence with business needs"
      ]
    }
  ]
});

// Export a singleton instance for easy access
let configInstance: ProfileConfig | null = null;

export const getProfileConfig = async (): Promise<ProfileConfig> => {
  if (!configInstance) {
    configInstance = await loadProfileConfig();
  }
  return configInstance;
}; 